import { formatDistanceToNow } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  Loader2Icon,
  Mail,
  MessageSquareIcon,
  RefreshCwIcon,
  XCircleIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import {
  useGetFrontConversationByDbIdQuery,
  useGetFrontConversationQuery,
} from '@/services/api/frontMessageService';
import {
  RainmakerEvidence,
  RainmakerEvidenceType,
} from '@/services/types/rainmaker-types';

interface EvidencePreviewProps {
  evidence: RainmakerEvidence;
  matterId: string;
}

const PreviewMedia = ({
  url,
  name,
  mimeType,
  isLoading,
  onLoad,
  onError,
}: {
  url: string;
  name: string;
  mimeType: string;
  isLoading: boolean;
  onLoad: () => void;
  onError: () => void;
}) =>
  mimeType.startsWith('image/') ? (
    <img
      src={url}
      alt={name}
      className={cn(
        'absolute inset-0 h-full w-full object-contain transition-opacity duration-500 z-[1]',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      onLoad={onLoad}
      onError={onError}
    />
  ) : (
    <iframe
      src={url}
      className={cn(
        'absolute inset-0 h-full w-full transition-opacity duration-500 z-[1]',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      title={name}
      onLoad={onLoad}
      onError={onError}
    />
  );

const TextPreview = ({ text }: { text: string }) => (
  <div className="p-3">
    <div className="rounded-md border bg-slate-50 p-3">
      <p className="whitespace-pre-wrap text-xs text-slate-900">{text}</p>
    </div>
  </div>
);

const ConversationPreview = ({
  conversation,
}: {
  conversation: { sender?: string; content: string }[];
}) => (
  <div className="space-y-2 p-3">
    {conversation.map((message, idx) => (
      <div key={idx} className="rounded-md border bg-slate-50 p-2">
        {message.sender && (
          <div className="mb-1 text-[10px] font-semibold text-slate-600">
            {message.sender}
          </div>
        )}
        <p className="text-xs text-slate-900">{message.content}</p>
      </div>
    ))}
  </div>
);

const FrontConversationPreview = ({
  matterId,
  conversationId,
}: {
  matterId: string;
  conversationId: string;
}) => {
  // Check if this looks like a UUID (database ID) or a Front API ID (msg_xxx, cnv_xxx)
  const isUUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      conversationId,
    );

  const {
    data: dataByDbId,
    isLoading: isLoadingByDbId,
    error: errorByDbId,
  } = useGetFrontConversationByDbIdQuery(
    {
      matterId,
      conversationDbId: conversationId,
    },
    {
      skip: !isUUID, // Only use this query if it's a UUID
    },
  );

  const {
    data: dataByApiId,
    isLoading: isLoadingByApiId,
    error: errorByApiId,
  } = useGetFrontConversationQuery(
    {
      matterId,
      conversationId,
    },
    {
      skip: isUUID, // Only use this query if it's NOT a UUID
    },
  );

  // Use whichever query is active
  const data = isUUID ? dataByDbId : dataByApiId;
  const isLoading = isUUID ? isLoadingByDbId : isLoadingByApiId;
  const error = isUUID ? errorByDbId : errorByApiId;

  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );

  // Get the Front conversation ID for the external link
  const frontConversationId = data?.conversation?.id;

  const handleOpenInFront = () => {
    if (frontConversationId) {
      window.open(
        `https://app.frontapp.com/open/${frontConversationId}`,
        '_blank',
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Loader2Icon className="h-5 w-5 animate-spin text-violet-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <XCircleIcon className="mx-auto h-6 w-6 text-slate-400" />
          <p className="mt-2 text-xs text-slate-600">
            Failed to load conversation
          </p>
        </div>
      </div>
    );
  }

  const toggleMessage = (messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header with Open in Front button */}
      {frontConversationId && (
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-[10px] font-medium text-slate-600">
            {data.messages.length} message
            {data.messages.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={handleOpenInFront}
            className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-violet-600 hover:bg-violet-50 transition-colors"
          >
            <ExternalLinkIcon className="h-3 w-3" />
            Open in Front
          </button>
        </div>
      )}

      {/* Messages list - scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-2 p-3">
          {data.messages.map((message, idx) => {
            const isExpanded = expandedMessages.has(message.id);
            const isLast = idx === data.messages.length - 1;
            const shouldExpand = isExpanded || isLast;

            const senderName = message.author
              ? `${message.author.first_name || ''} ${message.author.last_name || ''}`.trim() ||
                message.author.email
              : message.is_inbound
                ? 'External'
                : 'Unknown';

            const timeAgo = message.created_at
              ? formatDistanceToNow(new Date(message.created_at), {
                  addSuffix: true,
                })
              : null;

            return (
              <div
                key={message.id}
                className={cn(
                  'rounded-md border bg-white overflow-hidden',
                  message.is_inbound
                    ? 'border-l-4 border-l-blue-400'
                    : 'border-l-4 border-l-gray-300',
                )}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleMessage(message.id);
                  }}
                  className="w-full p-2 text-left hover:bg-slate-50 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Mail className="h-3 w-3 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold text-slate-900 truncate">
                          {senderName}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span
                            className={cn(
                              'text-[8px] font-medium px-1 py-0.5 rounded',
                              message.is_inbound
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {message.is_inbound ? 'IN' : 'OUT'}
                          </span>
                          {timeAgo && (
                            <span className="text-[9px] text-slate-500">
                              {timeAgo}
                            </span>
                          )}
                          {shouldExpand ? (
                            <ChevronDown className="h-3 w-3 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-slate-400" />
                          )}
                        </div>
                      </div>
                      {!shouldExpand && message.blurb && (
                        <p className="text-[9px] text-slate-500 mt-0.5 truncate">
                          {message.blurb}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                {shouldExpand && (
                  <div className="border-t bg-slate-50/50 p-2">
                    {message.body ? (
                      <div
                        className="text-[10px] text-slate-700 prose prose-sm max-w-none [&_*]:text-[10px]"
                        dangerouslySetInnerHTML={{ __html: message.body }}
                      />
                    ) : message.text ? (
                      <div className="text-[10px] text-slate-700 whitespace-pre-wrap">
                        {message.text}
                      </div>
                    ) : (
                      <p className="text-[9px] text-slate-500 italic">
                        No content
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const EvidencePreview = ({
  evidence,
  matterId,
}: EvidencePreviewProps) => {
  const [state, setState] = useState({
    isLoading: true,
    error: null as string | null,
    evidenceId: evidence.id,
  });

  const { isLoading, error, evidenceId } = state;

  // Fetch blob details if evidence type is BLOB or ARTIFACT
  const shouldFetchBlob =
    evidence.type === RainmakerEvidenceType.BLOB ||
    evidence.type === RainmakerEvidenceType.ARTIFACT ||
    evidence.type === RainmakerEvidenceType.DOCUMENT;

  const { data: blobData, isLoading: isBlobLoading } = useGetBlobDetailQuery(
    { matterId, blobId: evidence.id },
    { skip: !shouldFetchBlob },
  );

  // Reset loading state when evidence changes
  useEffect(() => {
    if (evidence.id !== evidenceId) {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        evidenceId: evidence.id,
      }));
    }
  }, [evidence.id, evidenceId]);

  const updateState = (newState: Partial<typeof state>) =>
    setState((prev) => ({ ...prev, ...newState }));

  const handlePreviewLoad = () => updateState({ isLoading: false });
  const handlePreviewError = () =>
    updateState({ isLoading: false, error: 'Failed to load preview' });

  const handleRetry = () => {
    updateState({ isLoading: true, error: null });
    setTimeout(() => {
      const iframe = document.querySelector('iframe');
      if (iframe && blobData?.download_url) {
        iframe.src = blobData.download_url;
      }
    }, 100);
  };

  // Handle BLOB/ARTIFACT/DOCUMENT types with blob preview
  if (shouldFetchBlob) {
    if (isBlobLoading) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <div className="flex flex-col items-center gap-2">
            <Loader2Icon className="h-5 w-5 animate-spin text-violet-500" />
            <span className="text-xs text-slate-500">Loading document...</span>
          </div>
        </div>
      );
    }

    if (!blobData) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <XCircleIcon className="h-6 w-6 text-slate-400" />
            <span className="text-xs text-slate-600">Document not found</span>
          </div>
        </div>
      );
    }

    const mimeType = blobData.file_mime_type || 'application/octet-stream';
    const isPreviewable =
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.includes('text/');

    if (!isPreviewable) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
          <div className="rounded-full bg-slate-100 p-3">
            <FileIcon className="h-6 w-6 text-slate-500" />
          </div>
          <div className="flex flex-col items-center text-center">
            <h4 className="text-xs font-semibold text-slate-900">
              {blobData.name}
            </h4>
            <p className="mt-1 text-[10px] text-slate-500">
              This file type cannot be previewed
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(blobData.download_url, '_blank')}
            className="mt-2 h-7 text-[10px]"
          >
            <DownloadIcon className="mr-1 h-3 w-3" />
            Download File
          </Button>
        </div>
      );
    }

    return (
      <div className="relative h-full">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-2 rounded-lg bg-white/80 p-3">
              <Loader2Icon className="h-4 w-4 animate-spin text-violet-500" />
              <span className="text-[10px] font-medium text-slate-600">
                Loading preview...
              </span>
              <Skeleton className="h-1.5 w-20 rounded-full" />
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95">
            <div className="flex max-w-[85%] flex-col items-center gap-2 text-center">
              <XCircleIcon className="h-6 w-6 text-red-500/70" />
              <span className="text-xs font-medium text-red-600">{error}</span>
              <p className="mb-1 text-[10px] text-slate-500">
                The file preview could not be loaded
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="h-7 text-[10px]"
                >
                  <RefreshCwIcon className="mr-1 h-3 w-3" />
                  Retry
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => window.open(blobData.download_url, '_blank')}
                  className="h-7 text-[10px]"
                >
                  <ExternalLinkIcon className="mr-1 h-3 w-3" />
                  Open in New Tab
                </Button>
              </div>
            </div>
          </div>
        )}

        <PreviewMedia
          url={blobData.download_url}
          name={blobData.name}
          mimeType={mimeType}
          isLoading={isLoading}
          onLoad={handlePreviewLoad}
          onError={handlePreviewError}
        />
      </div>
    );
  }

  // Handle text-based evidence types
  if (
    evidence.type === RainmakerEvidenceType.FRONT_MESSAGE ||
    evidence.type === RainmakerEvidenceType.MESSAGE ||
    evidence.type === RainmakerEvidenceType.EMAIL ||
    evidence.type === RainmakerEvidenceType.STAFF_NOTE ||
    evidence.type === RainmakerEvidenceType.INTAKE_NOTE
  ) {
    const content = (evidence.metadata?.content ||
      evidence.metadata?.body ||
      evidence.metadata?.text ||
      evidence.metadata?.note) as string | undefined;

    if (content) {
      return <TextPreview text={content} />;
    }
  }

  // Handle Front conversation types
  if (evidence.type === RainmakerEvidenceType.FRONT_CONVERSATION) {
    return (
      <FrontConversationPreview
        matterId={matterId}
        conversationId={evidence.id}
      />
    );
  }

  // Handle other conversation types
  if (
    evidence.type === RainmakerEvidenceType.CONVERSATION ||
    evidence.type === RainmakerEvidenceType.AUDIO_CONVERSATION
  ) {
    const messages = evidence.metadata?.messages as
      | { sender?: string; content: string }[]
      | undefined;

    if (messages && Array.isArray(messages)) {
      return <ConversationPreview conversation={messages} />;
    }
  }

  // Handle transcript
  if (evidence.type === RainmakerEvidenceType.TRANSCRIPT) {
    const transcript = evidence.metadata?.transcript as string | undefined;
    if (transcript) {
      return <TextPreview text={transcript} />;
    }
  }

  // Fallback: show metadata
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
      <div className="rounded-full bg-slate-100 p-3">
        <MessageSquareIcon className="h-6 w-6 text-slate-500" />
      </div>
      <div className="flex flex-col items-center text-center">
        <h4 className="text-xs font-semibold text-slate-900">
          {evidence.display_name || 'Evidence'}
        </h4>
        <p className="mt-1 text-[10px] text-slate-500">{evidence.type}</p>
      </div>
      {evidence.metadata && Object.keys(evidence.metadata).length > 0 && (
        <div className="mt-2 w-full space-y-2 text-left">
          {Object.entries(evidence.metadata).map(([key, value]) => (
            <div key={key} className="rounded border bg-slate-50 p-2">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">
                {key.replace(/_/g, ' ')}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-900">
                {typeof value === 'object'
                  ? JSON.stringify(value, null, 2)
                  : String(value)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
